import { StyleSheet, Text, View } from "@react-pdf/renderer";
import { Trans, useTranslation } from "react-i18next";

import { AnalysisReportPdfFooter } from "src/core/components/folders-list/pdf/AnalysisReportPdfFooter";
import { DualSegmentDistributionBlock } from "src/core/components/folders-list/pdf/DualSegmentDistributionBlock";
import { RiskDistributionBlock } from "src/core/components/folders-list/pdf/RiskDistributionBlock";
import { PdfActivityIcon } from "src/core/components/pdf/icons/radar/PdfActivityIcon";
import { PdfClientIcon } from "src/core/components/pdf/icons/radar/PdfClientIcon";
import { PdfLocationIcon } from "src/core/components/pdf/icons/radar/PdfLocationIcon";
import { PdfMissionIcon } from "src/core/components/pdf/icons/radar/PdfMissionIcon";
import { PdfHeader } from "src/core/components/pdf/shared/PdfHeader";
import { PdfPage } from "src/core/components/pdf/shared/PdfPage";
import { User } from "src/core/gql/graphql";
import { ValidatedFoldersStatistics } from "src/core/hooks/folders-list/folder-risk-statistics/types";

const styles = StyleSheet.create({
  sectionHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 36,
    marginBottom: 12,
    paddingHorizontal: 10,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#0A1A53",
  },
  sectionSubTitle: {
    fontSize: 11,
    color: "#0A1A53",
  },
});

type AnalysisReportSynthesisSectionProps = {
  currentUser: User;
  nbDossiers: number;
  statistics: ValidatedFoldersStatistics;
};

export const AnalysisReportSynthesisSection = ({
  currentUser,
  nbDossiers,
  statistics,
}: AnalysisReportSynthesisSectionProps) => {
  const { t } = useTranslation();

  return (
    <PdfPage>
      <PdfHeader />

      <View style={styles.sectionHeaderRow}>
        {/* Titre à gauche */}
        <Text style={styles.sectionTitle}>
          {t("pdf.validatedFolders.sections.synthesis.title")}
        </Text>

        {/* Sous-titre à droite */}
        <Text style={styles.sectionSubTitle}>
          {t("pdf.validatedFolders.sections.synthesis.nbDossiers", { nbDossiers })}
        </Text>
      </View>

      {/* Global */}
      <RiskDistributionBlock
        title={t("pdf.validatedFolders.sections.riskDistribution.global")}
        lowRisk={statistics.repartitionRisques.global.low}
        mediumRisk={statistics.repartitionRisques.global.medium}
        highRisk={statistics.repartitionRisques.global.high}
        description={<Trans i18nKey="pdf.validatedFolders.sections.synthesis.globalDescription" />}
        withBorder={true}
      />

      {/* Activité */}
      <RiskDistributionBlock
        title={t("pdf.validatedFolders.sections.riskDistribution.activity")}
        icon={PdfActivityIcon}
        lowRisk={statistics.repartitionRisques.activite.low}
        mediumRisk={statistics.repartitionRisques.activite.medium}
        highRisk={statistics.repartitionRisques.activite.high}
        description={t("pdf.validatedFolders.sections.synthesis.activityDescription")}
        withBorder={false}
      />

      {/* Localisation */}
      <RiskDistributionBlock
        title={t("pdf.validatedFolders.sections.riskDistribution.location")}
        icon={PdfLocationIcon}
        lowRisk={statistics.repartitionRisques.localisation.low}
        mediumRisk={statistics.repartitionRisques.localisation.medium}
        highRisk={statistics.repartitionRisques.localisation.high}
        description={t("pdf.validatedFolders.sections.synthesis.locationDescription")}
        withBorder={false}
      />

      {/* Client */}
      <RiskDistributionBlock
        title={t("pdf.validatedFolders.sections.riskDistribution.client")}
        icon={PdfClientIcon}
        lowRisk={statistics.repartitionRisques.client.low}
        mediumRisk={statistics.repartitionRisques.client.medium}
        highRisk={statistics.repartitionRisques.client.high}
        description={t("pdf.validatedFolders.sections.synthesis.clientDescription")}
        withBorder={false}
      />

      {/* Mission */}
      <RiskDistributionBlock
        title={t("pdf.validatedFolders.sections.riskDistribution.mission")}
        icon={PdfMissionIcon}
        lowRisk={statistics.repartitionRisques.mission.low}
        mediumRisk={statistics.repartitionRisques.mission.medium}
        highRisk={statistics.repartitionRisques.mission.high}
        description={t("pdf.validatedFolders.sections.synthesis.missionDescription")}
        withBorder={false}
      />

      <View style={styles.sectionHeaderRow}>
        {/* Titre à gauche */}
        <Text style={styles.sectionTitle}>
          {t("pdf.validatedFolders.sections.highRiskDistribution.title")}
        </Text>
      </View>

      <DualSegmentDistributionBlock
        leftValue={{
          count: statistics.repartitionRisquesEleves.sensibles.count,
          percentage: statistics.repartitionRisquesEleves.sensibles.percentage,
        }}
        rightValue={{
          count: statistics.repartitionRisquesEleves.nonSensibles.count,
          percentage: statistics.repartitionRisquesEleves.nonSensibles.percentage,
        }}
        leftTranslationKey="pdf.validatedFolders.sections.highRiskDistribution.highRisk"
        rightTranslationKey="pdf.validatedFolders.sections.highRiskDistribution.highRiskSensible"
        leftColor="#F6A890"
        rightColor="#C2BBE1"
        description={
          <Trans i18nKey="pdf.validatedFolders.sections.highRiskDistribution.description" />
        }
      />

      <View style={styles.sectionHeaderRow}>
        {/* Titre à gauche */}
        <Text style={styles.sectionTitle}>
          {t("pdf.validatedFolders.sections.modulationDistribution.title")}
        </Text>
      </View>

      <DualSegmentDistributionBlock
        leftValue={{
          count: statistics.repartitionModulation.nonModules.count,
          percentage: statistics.repartitionModulation.nonModules.percentage,
        }}
        rightValue={{
          count: statistics.repartitionModulation.modules.count,
          percentage: statistics.repartitionModulation.modules.percentage,
        }}
        leftTranslationKey="pdf.validatedFolders.sections.modulationDistribution.nonModules"
        rightTranslationKey="pdf.validatedFolders.sections.modulationDistribution.modules"
        leftColor="#7C9EAC"
        rightColor="#B3B9CC"
        description={
          <Trans i18nKey="pdf.validatedFolders.sections.modulationDistribution.description" />
        }
      />

      <AnalysisReportPdfFooter currentUser={currentUser} />
    </PdfPage>
  );
};
